import { c as _c } from "react/compiler-runtime";
// Оверлей обучающего тура: затемняет страницу, вырезает «прожектор» вокруг
// целевого элемента, показывает подсказку со стрелкой и кнопками навигации.
// Геометрия (куда поставить подсказку, как обрезать прожектор, как докрутить
// страницу) общая с кабинетом продавца — @osmix/ui-primitives/tour-layout.
// Подсказка сначала рисуется невидимой, чтобы снять её реальную высоту, и
// только потом ставится на место: так она не вылезает за экран.
import { Button } from "@osmix/ui/button";
import { computeTourLayout, type TourBox, type TourSide, tourSafeTop, trackTourTarget } from "@osmix/ui-primitives/tour-layout";
import { X } from "lucide-react";
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { TourStep } from "./tours";
const PADDING = 8; // отступ прожектора вокруг элемента
const TIP_WIDTH = 340;
const MARGIN = 12; // минимальный зазор от краёв экрана
const GAP = 14; // зазор между элементом и подсказкой
const ARROW = 12;

// Стрелка — повёрнутый квадрат; рамка только с двух внешних сторон, чтобы
// внутри подсказки не было видно её «хвоста».
const ARROW_EDGES: Record<TourSide, string> = {
  top: "border-l border-t",
  bottom: "border-r border-b",
  left: "border-l border-b",
  right: "border-r border-t"
};
const arrowStyle = (side: TourSide, offset: number): CSSProperties => {
  const half = ARROW / 2;
  if (side === "top") return {
    top: -half - 1,
    left: offset - half
  };
  if (side === "bottom") return {
    bottom: -half - 1,
    left: offset - half
  };
  if (side === "left") return {
    left: -half - 1,
    top: offset - half
  };
  return {
    right: -half - 1,
    top: offset - half
  };
};
export function GuidedTour(t0) {
  const $ = _c(126);
  const {
    steps,
    step,
    onNext,
    onPrev,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const tipRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [located, setLocated] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: TIP_WIDTH,
      height: 180
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [tipSize, setTipSize] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [viewport, setViewport] = useState(t2);
  const [shown, setShown] = useState(false);
  const total = steps.length;
  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === total - 1;
  let t3;
  if ($[2] !== current || $[3] !== tipSize) {
    t3 = () => {
      if (!current) {
        return;
      }
      setLocated(false);
      return trackTourTarget(current.target, next => {
        setRect(next);
        setLocated(true);
        setViewport({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, {
        tipHeight: tipSize.height
      });
    };
    $[2] = current;
    $[3] = tipSize;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== current) {
    t4 = [current];
    $[5] = current;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const el = tipRef.current;
      if (!el) {
        return;
      }
      const measure = () => {
        const r = el.getBoundingClientRect();
        setTipSize(prev => prev.width === r.width && prev.height === r.height ? prev : {
          width: r.width,
          height: r.height
        });
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    };
    t6 = [];
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    };
    t8 = [];
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[11] !== onClose || $[12] !== onNext || $[13] !== onPrev) {
    t9 = () => {
      const onKey = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
        } else {
          if (event.key === "ArrowRight" || event.key === "Enter") {
            event.preventDefault();
            onNext();
          } else {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              onPrev();
            }
          }
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    };
    t10 = [onNext, onPrev, onClose];
    $[11] = onClose;
    $[12] = onNext;
    $[13] = onPrev;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  if (!current) {
    return null;
  }
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[16] !== current.titleKey || $[17] !== located || $[18] !== onClose || $[19] !== rect || $[20] !== shown || $[21] !== t || $[22] !== tipSize || $[23] !== viewport) {
    const layout = computeTourLayout(rect, tipSize, viewport, {
      padding: PADDING,
      gap: GAP,
      margin: MARGIN,
      safeTop: tourSafeTop()
    });
    const visible = shown && located;
    t24 = createPortal;
    t20 = "guided-tour";
    const t25 = shown ? "opacity-100" : "opacity-0";
    if ($[38] !== t25) {
      t21 = cn("fixed inset-0 z-[100] transition-opacity duration-200", t25);
      $[38] = t25;
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    t22 = layout.spotlight ? <div aria-hidden={true} data-tour-spotlight={true} className="pointer-events-none absolute rounded-lg ring-2 ring-primary/70 transition-all duration-300 ease-out" style={{
      ...layout.spotlight,
      boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)"
    }} /> : <div aria-hidden={true} className="absolute inset-0 bg-slate-900/55" />;
    let t26;
    if ($[40] !== t) {
      t26 = t("tour.skip");
      $[40] = t;
      $[41] = t26;
    } else {
      t26 = $[41];
    }
    if ($[42] !== onClose || $[43] !== t26) {
      t23 = <button type="button" aria-label={t26} className="absolute inset-0 h-full w-full cursor-default" onClick={onClose} />;
      $[42] = onClose;
      $[43] = t26;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    t11 = tipRef;
    t12 = "dialog";
    t13 = "true";
    if ($[45] !== current.titleKey || $[46] !== t) {
      t14 = t(current.titleKey);
      $[45] = current.titleKey;
      $[46] = t;
      $[47] = t14;
    } else {
      t14 = $[47];
    }
    t15 = true;
    t16 = layout.placement;
    const t27 = visible ? "opacity-100" : "opacity-0";
    const t28 = located && "transition-[top,left,opacity] duration-200 ease-out";
    if ($[48] !== t27 || $[49] !== t28) {
      t17 = cn("absolute flex max-h-[calc(100vh-1.5rem)] flex-col rounded-xl border bg-popover p-4 text-popover-foreground shadow-2xl", t27, t28);
      $[48] = t27;
      $[49] = t28;
      $[50] = t17;
    } else {
      t17 = $[50];
    }
    t18 = {
      top: layout.tip.top,
      left: layout.tip.left,
      width: `min(${TIP_WIDTH}px, calc(100vw - ${MARGIN * 2}px))`
    };
    t19 = layout.arrow && <span aria-hidden={true} className={cn("absolute size-3 rotate-45 rounded-[2px] border-border bg-popover", ARROW_EDGES[layout.arrow.side])} style={arrowStyle(layout.arrow.side, layout.arrow.offset)} />;
    $[16] = current.titleKey;
    $[17] = located;
    $[18] = onClose;
    $[19] = rect;
    $[20] = shown;
    $[21] = t;
    $[22] = tipSize;
    $[23] = viewport;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
    $[37] = t24;
  } else {
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t17 = $[30];
    t18 = $[31];
    t19 = $[32];
    t20 = $[33];
    t21 = $[34];
    t22 = $[35];
    t23 = $[36];
    t24 = $[37];
  }
  const t25 = `${(step + 1) / total * 100}%`;
  let t26;
  if ($[51] !== t25) {
    t26 = <div aria-hidden={true} className="absolute inset-x-0 top-0 h-0.5 overflow-hidden rounded-t-xl"><div className="h-full bg-primary transition-[width] duration-300" style={{
        width: t25
      }} /></div>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== current.titleKey || $[54] !== t) {
    t27 = t(current.titleKey);
    $[53] = current.titleKey;
    $[54] = t;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t27) {
    t28 = <h3 className="text-sm font-semibold leading-snug">{t27}</h3>;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t) {
    t29 = t("tour.skip");
    $[58] = t;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== t) {
    t30 = t("tour.skip");
    $[60] = t;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <X className="size-4" />;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== onClose || $[64] !== t29 || $[65] !== t30) {
    t32 = <button type="button" onClick={onClose} aria-label={t29} title={t30} className="-mr-1.5 -mt-1.5 grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">{t31}</button>;
    $[63] = onClose;
    $[64] = t29;
    $[65] = t30;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== t28 || $[68] !== t32) {
    t33 = <div className="relative flex items-start justify-between gap-3">{t28}{t32}</div>;
    $[67] = t28;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== current.textKey || $[71] !== t) {
    t34 = t(current.textKey);
    $[70] = current.textKey;
    $[71] = t;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t34) {
    t35 = <p className="relative mt-1.5 min-h-0 overflow-y-auto text-sm leading-relaxed text-muted-foreground">{t34}</p>;
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  const t36 = step + 1;
  let t37;
  if ($[75] !== t || $[76] !== t36 || $[77] !== total) {
    t37 = t("tour.step", {
      current: t36,
      total
    });
    $[75] = t;
    $[76] = t36;
    $[77] = total;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== t37) {
    t38 = <div className="flex min-w-0 items-center"><span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{t37}</span></div>;
    $[79] = t37;
    $[80] = t38;
  } else {
    t38 = $[80];
  }
  const t39 = isFirst && "invisible";
  let t40;
  if ($[81] !== t39) {
    t40 = cn(t39);
    $[81] = t39;
    $[82] = t40;
  } else {
    t40 = $[82];
  }
  const t41 = isFirst ? -1 : undefined;
  const t42 = isFirst || undefined;
  let t43;
  if ($[83] !== t) {
    t43 = t("tour.back");
    $[83] = t;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  let t44;
  if ($[85] !== onPrev || $[86] !== t40 || $[87] !== t41 || $[88] !== t42 || $[89] !== t43) {
    t44 = <Button variant="outline" size="sm" onClick={onPrev} className={t40} tabIndex={t41} aria-hidden={t42}>{t43}</Button>;
    $[85] = onPrev;
    $[86] = t40;
    $[87] = t41;
    $[88] = t42;
    $[89] = t43;
    $[90] = t44;
  } else {
    t44 = $[90];
  }
  let t45;
  if ($[91] !== isLast || $[92] !== t) {
    t45 = isLast ? t("tour.done") : t("tour.next");
    $[91] = isLast;
    $[92] = t;
    $[93] = t45;
  } else {
    t45 = $[93];
  }
  let t46;
  if ($[94] !== onNext || $[95] !== t45) {
    t46 = <Button size="sm" onClick={onNext} className="min-w-[4.75rem]">{t45}</Button>;
    $[94] = onNext;
    $[95] = t45;
    $[96] = t46;
  } else {
    t46 = $[96];
  }
  let t47;
  if ($[97] !== t44 || $[98] !== t46) {
    t47 = <div className="flex shrink-0 items-center gap-2">{t44}{t46}</div>;
    $[97] = t44;
    $[98] = t46;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] !== t38 || $[101] !== t47) {
    t48 = <div className="relative mt-4 flex items-center justify-between gap-3">{t38}{t47}</div>;
    $[100] = t38;
    $[101] = t47;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] !== t11 || $[104] !== t12 || $[105] !== t13 || $[106] !== t14 || $[107] !== t15 || $[108] !== t16 || $[109] !== t17 || $[110] !== t18 || $[111] !== t19 || $[112] !== t26 || $[113] !== t33 || $[114] !== t35 || $[115] !== t48) {
    t49 = <div ref={t11} role={t12} aria-modal={t13} aria-label={t14} data-tour-tip={t15} data-placement={t16} className={t17} style={t18}>{t19}{t26}{t33}{t35}{t48}</div>;
    $[103] = t11;
    $[104] = t12;
    $[105] = t13;
    $[106] = t14;
    $[107] = t15;
    $[108] = t16;
    $[109] = t17;
    $[110] = t18;
    $[111] = t19;
    $[112] = t26;
    $[113] = t33;
    $[114] = t35;
    $[115] = t48;
    $[116] = t49;
  } else {
    t49 = $[116];
  }
  let t50;
  if ($[117] !== t20 || $[118] !== t21 || $[119] !== t22 || $[120] !== t23 || $[121] !== t49) {
    t50 = <div data-testid={t20} className={t21}>{t22}{t23}{t49}</div>;
    $[117] = t20;
    $[118] = t21;
    $[119] = t22;
    $[120] = t23;
    $[121] = t49;
    $[122] = t50;
  } else {
    t50 = $[122];
  }
  let t51;
  if ($[123] !== t24 || $[124] !== t50) {
    t51 = t24(t50, document.body);
    $[123] = t24;
    $[124] = t50;
    $[125] = t51;
  } else {
    t51 = $[125];
  }
  return t51;
}